import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check } from 'lucide-react';
import PublicHeader from '../components/Layout/PublicHeader';
import StaticPageTopbar from '../components/Layout/StaticPageTopbar';
import { track, trackOnce } from '../services/trackingService';
import { umamiTrack } from '../services/analytics';
import { SITE_ORIGIN } from '../config/siteOrigin';

// ── „Logo & Verlinkung" ───────────────────────────────────────────────────────────────────
// Vereine und Veranstalter verlinken gern grafisch auf uns — finden sie dafür nichts, verlinken
// sie im Zweifel gar nicht, statt nachzufragen. Die Seite nimmt ihnen beide Schritte ab: Logo
// herunterladen *und* fertiger Baustein zum Einfügen. Der Baustein ist der eigentliche Zweck;
// eine reine Bilddatei landet erfahrungsgemäß in keinem Seitenkopf.
//
// Alles wird gezählt (First-Party-Tracking, Entität PAGE/'logo'), weil sich sonst nicht sagen
// lässt, ob die Seite etwas bringt — genau das war vorher nicht möglich.

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";

const PAGE_TITLE = 'Logo & Verlinkung – Auf Radsport Events verlinken | Radsport Events';
const PAGE_DESC =
  'Logo von Radsport Events zum Download plus fertige Bausteine zum Einfügen: So verlinkt euer Verein oder eure Veranstaltung mit einem Klick auf unseren Radsport-Terminkalender.';
const CANONICAL = `${SITE_ORIGIN}/logo`;

/** Alt-Text und Titel der Bausteine — außerhalb der Vorlagen, damit sie überall gleich lauten. */
const LINK_TITEL = 'Radsport Events – Termine für RTF, Marathon, Gravel und MTB';

interface Datei {
  id: string;
  datei: string;
  label: string;
  hinweis: string;
  breite: number;
  hoehe: number;
  /** Vorschau auf dunklem Grund, weil die helle Fassung auf Weiß unsichtbar wäre. */
  dunkel?: boolean;
}

const DATEIEN: Datei[] = [
  {
    id: 'datei-standard', datei: 'radsport-events-logo.png', label: 'Standard', breite: 810, hoehe: 136,
    hinweis: 'Für helle Hintergründe. 810 × 136 px, transparent.',
  },
  {
    id: 'datei-klein', datei: 'radsport-events-logo-300.png', label: 'Klein', breite: 299, hoehe: 51,
    hinweis: 'Dasselbe in 299 × 51 px — passt ohne Verkleinern in eine Seitenspalte.',
  },
  {
    id: 'datei-standard-hell', datei: 'radsport-events-logo-weiss.png', label: 'Standard, hell', breite: 810, hoehe: 136,
    hinweis: 'Für dunkle Hintergründe. 810 × 136 px, transparent.', dunkel: true,
  },
  {
    id: 'datei-klein-hell', datei: 'radsport-events-logo-weiss-300.png', label: 'Klein, hell', breite: 299, hoehe: 51,
    hinweis: 'Die helle Fassung in 299 × 51 px.', dunkel: true,
  },
];

const BAUSTEINE: { id: string; label: string; erklaerung: string; code: string }[] = [
  {
    id: 'html',
    label: 'Logo mit Link (HTML)',
    erklaerung: 'Für die eigene Webseite. Zeigt das Logo und verlinkt auf unsere Startseite.',
    code:
      `<a href="${SITE_ORIGIN}/" title="${LINK_TITEL}">\n` +
      `  <img src="${SITE_ORIGIN}/logo/radsport-events-logo-300.png"\n` +
      `       alt="${LINK_TITEL}" width="299" height="51">\n` +
      `</a>`,
  },
  {
    id: 'textlink',
    label: 'Nur ein Textlink (HTML)',
    erklaerung: 'Wenn kein Platz für ein Bild ist — etwa in einer Linkliste.',
    code: `<a href="${SITE_ORIGIN}/">${LINK_TITEL}</a>`,
  },
  {
    id: 'markdown',
    label: 'Logo mit Link (Markdown)',
    erklaerung: 'Für Wikis, Foren und alles, was Markdown versteht.',
    code: `[![Radsport Events](${SITE_ORIGIN}/logo/radsport-events-logo-300.png)](${SITE_ORIGIN}/)`,
  },
];

// ── Banner für die E-Mail-Signatur ───────────────────────────────────────────────────────
// Vereine schreiben ihren Teilnehmern ohnehin: Startunterlagen, Ergebnisse, Einladungen. Ein
// Banner unter der Signatur führt von dort zu uns. Das Bild liegt auf unserem Server, weil
// Mailprogramme Signaturbilder sonst als Anhang an jede Mail hängen (Gmail nimmt in der Signatur
// ohnehin nur Bilder per Adresse). Die Adressen erzeugt brand/export.js; sie stehen danach in
// fremden Signaturen und werden deshalb nie umbenannt.
//
// Kopiert wird als formatierter Inhalt (text/html), weil sich der in die Signatur-Editoren von
// Apple Mail, Gmail und Outlook einfügen lässt; den HTML-Code brauchen nur Programme, die ihn
// ausdrücklich verlangen. Der Link trägt UTM-Parameter, damit Umami die Besuche aus Signaturen
// und die beiden Texte auseinanderhalten kann.

interface Banner { id: string; datei: string; label: string; alt: string }

const SIGNATUR_BANNER: Banner[] = [
  { id: 'weitere-events', datei: 'signatur-banner-weitere-events.png', label: 'Finde weitere Events', alt: 'Finde weitere Events auf radsport-events.de' },
  { id: 'naechstes-event', datei: 'signatur-banner-naechstes-event.png', label: 'Finde dein nächstes Event', alt: 'Finde dein nächstes Event auf radsport-events.de' },
];

const bannerCode = (b: Banner) =>
  `<a href="${SITE_ORIGIN}/?utm_source=signatur&amp;utm_medium=email&amp;utm_campaign=${b.id}" target="_blank">` +
  `<img src="${SITE_ORIGIN}/logo/${b.datei}" alt="${b.alt}" width="600" height="120" ` +
  `style="display:block;width:600px;max-width:100%;height:auto;border:0"></a>`;

/** Als formatierten Inhalt kopieren; ohne ClipboardItem (ältere Browser) wenigstens den Code. */
async function kopiereFormatiert(html: string): Promise<void> {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
    await navigator.clipboard.write([new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([html], { type: 'text/plain' }),
    })]);
  } else {
    await navigator.clipboard.writeText(html);
  }
}

const Logo: React.FC = () => {
  const [kopiert, setKopiert] = useState<string | null>(null);
  const [banner, setBanner] = useState<Banner>(SIGNATUR_BANNER[0]);

  useEffect(() => {
    trackOnce('PAGE', 'logo', 'VIEW');
    umamiTrack('logo-seite');
  }, []);

  const merkeDownload = (id: string, datei: string) => {
    track('PAGE', 'logo', 'DOWNLOAD', id);
    umamiTrack('logo-download', { datei });
  };

  const kopiere = async (id: string, code: string, formatiert = false) => {
    try {
      if (formatiert) await kopiereFormatiert(code);
      else await navigator.clipboard.writeText(code);
      setKopiert(id);
      window.setTimeout(() => setKopiert(k => (k === id ? null : k)), 2200);
    } catch {
      // Kein Zugriff auf die Zwischenablage (älterer Browser, kein HTTPS): Der Code steht
      // sichtbar da und lässt sich von Hand markieren — eine Fehlermeldung hülfe hier nicht.
      setKopiert(null);
    }
    track('PAGE', 'logo', 'CLICK', `baustein-${id}`);
    umamiTrack('logo-baustein-kopiert', { baustein: id });
  };

  const kopierKnopf = (id: string, code: string, label: string, formatiert = false, haupt = false) => (
    <button
      type="button"
      onClick={() => kopiere(id, code, formatiert)}
      style={{
        fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, cursor: 'pointer',
        padding: '8px 14px', borderRadius: 999, whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        border: '1px solid #922C57',
        background: haupt || kopiert === id ? '#922C57' : '#fff',
        color: haupt || kopiert === id ? '#fff' : '#922C57',
        ...(haupt ? { fontSize: 14, padding: '10px 18px' } : {}),
      }}
    >
      {kopiert === id ? <>Kopiert <Check size={14} aria-hidden /></> : label}
    </button>
  );

  const karte: React.CSSProperties = {
    background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14,
    boxShadow: '0 1px 3px rgba(38,38,38,0.06)',
  };
  const h2: React.CSSProperties = {
    fontFamily: FONT_DISPLAY, fontSize: 30, fontWeight: 800, color: '#2C2C2C', margin: '0 0 10px',
  };
  const fliess: React.CSSProperties = {
    fontFamily: FONT_BODY, fontSize: 16, lineHeight: 1.65, color: '#4A4A4A', margin: 0,
  };
  const monoLabel: React.CSSProperties = {
    fontFamily: FONT_MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: '#9A9494',
  };

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
      </Helmet>

      <style>{`
        .lg-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
        .lg-code {
          font-family: 'Space Mono', monospace; font-size: 12.5px; line-height: 1.6;
          background: #2C2C2C; color: #E8E4E4; border-radius: 10px; padding: 14px 16px;
          margin: 0;
          /* Umbrechen statt seitlich wegzuscrollen: abgeschnittener Code sieht unvollstaendig
             aus, und kopiert wird ohnehin ueber den Knopf, nicht mit der Maus. */
          white-space: pre-wrap; word-break: break-word;
        }
        @media (max-width: 780px) {
          .lg-container { padding: 0 20px 56px !important; }
          .lg-grid { grid-template-columns: 1fr; }
          .lg-h1 { font-size: 32px !important; }
        }
      `}</style>

      <div className="hide-mobile">
        <PublicHeader />
      </div>
      <StaticPageTopbar label="// LOGO & VERLINKUNG" />

      <div className="lg-container" style={{ flex: 1, maxWidth: 900, width: '100%', margin: '0 auto', padding: '0 40px 72px' }}>

        <section style={{ padding: '38px 0 8px' }}>
          <h1 className="lg-h1" style={{ fontFamily: FONT_DISPLAY, fontSize: 42, fontWeight: 800, color: '#2C2C2C', margin: '0 0 14px', letterSpacing: '-0.01em' }}>
            Auf Radsport Events verlinken
          </h1>
          <p style={{ ...fliess, fontSize: 17 }}>
            Ihr wollt von eurer Vereins- oder Veranstaltungsseite auf unseren Terminkalender
            verlinken? Gern. Hier stehen das Logo zum Herunterladen, fertige Bausteine, die ihr
            nur noch einfügen müsst, und ein Banner für eure E-Mail-Signatur. Eine Genehmigung braucht ihr dafür nicht.
          </p>
        </section>

        {/* BAUSTEINE zuerst: Wer verlinken will, ist damit in einem Schritt fertig — die
            Bilddatei allein bedeutete für die meisten noch Arbeit. */}
        <section style={{ ...karte, padding: 28, marginTop: 26 }}>
          <div style={{ ...monoLabel, marginBottom: 12 }}>ZUM EINFÜGEN</div>
          <h2 style={h2}>Fertige Bausteine</h2>
          <p style={{ ...fliess, marginBottom: 22 }}>
            Kopieren, in die eigene Seite einfügen, fertig. Das Logo liegt dabei auf unserem Server —
            ihr müsst nichts hochladen, und es bleibt automatisch aktuell.
          </p>

          {BAUSTEINE.map(b => (
            <div key={b.id} style={{ marginBottom: 22 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                <div>
                  <div style={{ fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, color: '#2C2C2C' }}>{b.label}</div>
                  <div style={{ fontFamily: FONT_BODY, fontSize: 13.5, color: '#6A6A6A' }}>{b.erklaerung}</div>
                </div>
                {kopierKnopf(b.id, b.code, 'Kopieren')}
              </div>
              <pre className="lg-code"><code>{b.code}</code></pre>
            </div>
          ))}

          <div style={{ borderTop: '1px solid #EDE9E9', paddingTop: 18, marginTop: 4 }}>
            <div style={{ ...monoLabel, marginBottom: 10 }}>SO SIEHT DAS AUS</div>
            <div style={{ background: '#F5F3F3', border: '1px dashed #D9D3D3', borderRadius: 10, padding: 20, display: 'flex', justifyContent: 'center' }}>
              <img src="/logo/radsport-events-logo-300.png" alt={LINK_TITEL} width={299} height={51} style={{ display: 'block', maxWidth: '100%', height: 'auto' }} />
            </div>
          </div>
        </section>

        {/* SIGNATUR */}
        <section style={{ ...karte, padding: 28, marginTop: 22 }}>
          <div style={{ ...monoLabel, marginBottom: 12 }}>FÜR EURE MAILS</div>
          <h2 style={h2}>Banner für die Signatur</h2>
          <p style={{ ...fliess, marginBottom: 22 }}>
            Ihr schreibt euren Teilnehmern ohnehin, etwa mit den Startunterlagen oder den
            Ergebnissen. Mit dem Banner unter eurer Signatur finden sie von dort aus weitere Events.
            Wählt einen der beiden Texte, kopiert das Banner und fügt es in den Einstellungen eures
            Mailprogramms bei der Signatur ein.
          </p>

          <div className="lg-grid" role="radiogroup" aria-label="Text des Banners">
            {SIGNATUR_BANNER.map(b => {
              const gewaehlt = banner.id === b.id;
              return (
                <button
                  key={b.id}
                  type="button"
                  role="radio"
                  aria-checked={gewaehlt}
                  onClick={() => setBanner(b)}
                  style={{
                    textAlign: 'left', cursor: 'pointer', background: '#fff', padding: 12,
                    borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 10,
                    border: gewaehlt ? '2px solid #922C57' : '1px solid #E5E1E1',
                    margin: gewaehlt ? 0 : 1,
                  }}
                >
                  <img src={`/logo/${b.datei}`} alt={b.alt} width={600} height={120}
                    style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 4 }} />
                  <span style={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, color: gewaehlt ? '#922C57' : '#2C2C2C', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {gewaehlt && <Check size={15} aria-hidden />}
                    {b.label}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{ margin: '22px 0 26px' }}>
            {kopierKnopf(`signatur-${banner.id}`, bannerCode(banner), 'Banner kopieren', true, true)}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
            <div>
              <div style={{ fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, color: '#2C2C2C' }}>Als HTML-Code</div>
              <div style={{ fontFamily: FONT_BODY, fontSize: 13.5, color: '#6A6A6A' }}>Für Programme, die in der Signatur Code erwarten, etwa Thunderbird.</div>
            </div>
            {kopierKnopf(`signatur-code-${banner.id}`, bannerCode(banner), 'Kopieren')}
          </div>
          <pre className="lg-code"><code>{bannerCode(banner)}</code></pre>
        </section>

        {/* DOWNLOADS */}
        <section style={{ ...karte, padding: 28, marginTop: 22 }}>
          <div style={{ ...monoLabel, marginBottom: 12 }}>DATEIEN</div>
          <h2 style={h2}>Logo herunterladen</h2>
          <p style={{ ...fliess, marginBottom: 22 }}>
            PNG mit transparentem Hintergrund. Die helle Fassung ist für dunkle Hintergründe
            gedacht — auf Weiß verschwindet sie.
          </p>

          <div className="lg-grid">
            {DATEIEN.map(d => (
              <div key={d.id} style={{ border: '1px solid #E5E1E1', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  background: d.dunkel ? '#2C2C2C' : '#fff', padding: '22px 18px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 96,
                }}>
                  {/* Echte Bildmasse mitgeben: sonst springt das Layout, waehrend die vier
                      Vorschauen laden. */}
                  <img src={`/logo/${d.datei}`} alt={`Logo Radsport Events — ${d.label}`}
                    width={d.breite} height={d.hoehe}
                    style={{ display: 'block', width: '100%', maxWidth: 230, height: 'auto' }} />
                </div>
                <div style={{ padding: '14px 16px 16px', borderTop: '1px solid #E5E1E1', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                  <div>
                    <div style={{ fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, color: '#2C2C2C' }}>{d.label}</div>
                    <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#6A6A6A', lineHeight: 1.5 }}>{d.hinweis}</div>
                  </div>
                  <a
                    href={`/logo/${d.datei}`}
                    download={d.datei}
                    onClick={() => merkeDownload(d.id, d.datei)}
                    style={{
                      marginTop: 'auto', textAlign: 'center', textDecoration: 'none',
                      fontFamily: FONT_BODY, fontSize: 13.5, fontWeight: 700,
                      padding: '9px 14px', borderRadius: 999,
                      background: '#922C57', color: '#fff',
                    }}
                  >
                    Herunterladen (PNG)
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* NUTZUNG */}
        <section style={{ ...karte, padding: 28, marginTop: 22 }}>
          <div style={{ ...monoLabel, marginBottom: 12 }}>SPIELREGELN</div>
          <h2 style={h2}>Was ihr damit machen dürft</h2>
          {/* listStyle ausdruecklich: der globale Reset in App.css nimmt Aufzaehlungen die Punkte,
              ohne sie liest sich der eingerueckte Block wie zusammenhanglose Absaetze. */}
          <ul style={{ ...fliess, paddingLeft: 22, margin: 0, listStyle: 'disc' }}>
            <li style={{ marginBottom: 8 }}>
              Das Logo verwenden, um auf Radsport Events zu verlinken — auf eurer Webseite, in
              Newslettern, in sozialen Netzen. Dafür ist es da.
            </li>
            <li style={{ marginBottom: 8 }}>
              Bitte in den Proportionen lassen und die Farben nicht ändern. Wenn ihr eine andere
              Größe braucht, skaliert es gleichmäßig — oder schreibt uns kurz.
            </li>
            <li style={{ marginBottom: 8 }}>
              Nicht verwenden, wenn dadurch der Eindruck entsteht, wir wären Veranstalter,
              Sponsor oder Partner eures Angebots. Ein Link ist ein Link.
            </li>
            <li>
              Ihr braucht ein anderes Format, etwa fürs Vereinsheft oder ein Trikot?{' '}
              <a href="/kontakt" style={{ color: '#922C57', fontWeight: 600 }}>Schreibt uns</a> —
              wir schicken es euch.
            </li>
          </ul>
        </section>
      </div>

    </div>
  );
};

export default Logo;
